import CustomButton from '@/components/atoms/CustomButton';
import svgIcons from '@/assets/svg/icons';
import { TContentType } from '@/types/Content.types';
import { Box, Button, HStack, Image, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  hasDetailsError,
  keepsExistingRemoteVideo,
  validateContentStep,
  validateDetails,
  validateForSubmit,
} from '@/modules/content/creation/model/validation';
import {
  TContentFormErrors,
  TContentFormMode,
  TContentFormSubmit,
  TContentFormValues,
  WizardFinalAction,
} from '@/modules/content/creation/model/types';
import { useContentForm } from '@/modules/content/creation/model/useContentForm';
import { collectImageUploads } from '@/modules/content/creation/model/contentPayload';
import { validateArticleSections } from '@/modules/content/creation/model/articleValidation';
import { getContentTypeLabel } from '@/modules/content/common/contentLabels';
import ArticleSectionsEditor from '@/modules/content/creation/steps/ArticleSectionsEditor';
import { hasArticleFieldError } from '@/modules/content/creation/model/articleValidation';
import DetailsStep from '@/modules/content/creation/steps/DetailsStep';
import PreviewStep from '@/modules/content/creation/steps/PreviewStep';
import { text } from '@/theme/colors/palettes';

export type { TContentFormMode } from '@/modules/content/creation/model/types';

// Articles get an extra read-only "arrange" step (manage images / sections) between
// editing and preview; posts go straight to preview.
type WizardStep = 'details' | 'content' | 'arrange' | 'preview';

// The content step is labelled with the content type itself (Post / Article / Reel).
const stepTitle = (step: WizardStep, type: TContentType): string => {
  switch (step) {
    case 'details':
      return 'Add Thumbnail and Details';
    case 'content':
      return getContentTypeLabel(type);
    case 'arrange':
      return 'Arrange';
    case 'preview':
      return 'Content Preview';
  }
};

type Props = {
  type: TContentType;
  authorLabel: string;
  authorPhotoUrl?: string | null;
  defaultValues?: Partial<TContentFormValues>;
  // Existing thumbnail URL (edit mode), shown until a new file is picked.
  defaultThumbnailUrl?: string | null;
  canRemoveThumbnail?: boolean;
  allowOrganizationVisibility?: boolean;
  isSubmitting: boolean;
  // "Go Back" from the first step (e.g. back to the list).
  onExit: () => void;
  onSubmit: (mode: TContentFormMode, data: TContentFormSubmit) => Promise<boolean | void> | void;
  // Terminal action on the Preview step (e.g. "Publish Content").
  primaryAction: WizardFinalAction;
  // Optional action on the Content step to save without previewing (e.g. "Save draft").
  secondaryAction?: WizardFinalAction;
  onDeleteDraft?: () => void;
  isDeletingDraft?: boolean;
  // Edit flows should not advance until the creator has changed authored data.
  requireChangesToContinue?: boolean;
  isVideoSourceReadOnly?: boolean;
  isCreateFlow?: boolean;
  isEditingDraft?: boolean;
};

const editableSnapshot = (values: TContentFormValues) => ({
  title: values.title,
  description: values.description,
  domains: values.domains,
  subdomains: values.subdomains,
  isPublic: values.isPublic,
  body: values.body,
  sections: values.sections,
  videoSource: values.videoSource,
  videoFile: values.videoFile,
  youtubeUrl: values.youtubeUrl,
  fullDescription: values.fullDescription,
});

const ContentWizard = ({
  type,
  authorLabel,
  authorPhotoUrl,
  defaultValues,
  defaultThumbnailUrl,
  canRemoveThumbnail = true,
  allowOrganizationVisibility = true,
  isSubmitting,
  onExit,
  onSubmit,
  primaryAction,
  secondaryAction,
  onDeleteDraft,
  isDeletingDraft = false,
  requireChangesToContinue = false,
  isVideoSourceReadOnly = false,
  isCreateFlow = false,
  isEditingDraft = false,
}: Props) => {
  const isCreateDraftComposer = isCreateFlow || isEditingDraft;
  const isCreatePost = isCreateDraftComposer && type === 'post';
  const isCreateReel = isCreateDraftComposer && type === 'video';
  const isCreateArticle = isCreateDraftComposer && type === 'article';
  const [step, setStep] = useState<WizardStep>('details');
  const [isVideoCompressing, setIsVideoCompressing] = useState(false);
  const [isYoutubeDurationChecking, setIsYoutubeDurationChecking] = useState(false);
  const {
    values,
    errors,
    setErrors,
    setField,
    setSections,
    setDomains,
    setSubdomains,
    thumbnailFile,
    setThumbnailFile,
  } = useContentForm(defaultValues);
  const initialEditableValues = useRef(JSON.stringify(editableSnapshot(values)));
  const [savedDraftSnapshot, setSavedDraftSnapshot] = useState(initialEditableValues.current);
  const [savedDraftThumbnail, setSavedDraftThumbnail] = useState<File | null>(null);
  const [thumbnailRemoved, setThumbnailRemoved] = useState(false);
  const [savedDraftVideoFile, setSavedDraftVideoFile] = useState<File | null>(null);
  const [savedDraftArticleFiles, setSavedDraftArticleFiles] = useState<File[]>([]);
  const articleFiles = collectImageUploads(values).map(({ file }) => file);
  const hasAuthoredChanges = useMemo(
    () =>
      isCreateDraftComposer || isEditingDraft
        ? thumbnailFile !== savedDraftThumbnail ||
          thumbnailRemoved ||
          values.videoFile !== savedDraftVideoFile ||
          articleFiles.length !== savedDraftArticleFiles.length ||
          articleFiles.some((file, index) => file !== savedDraftArticleFiles[index]) ||
          JSON.stringify(editableSnapshot(values)) !== savedDraftSnapshot
        : Boolean(thumbnailFile) ||
          thumbnailRemoved ||
          Boolean(values.videoFile) ||
          JSON.stringify(editableSnapshot(values)) !== initialEditableValues.current,
    [
      isCreateDraftComposer,
      isEditingDraft,
      savedDraftSnapshot,
      savedDraftThumbnail,
      savedDraftVideoFile,
      savedDraftArticleFiles,
      articleFiles,
      thumbnailFile,
      thumbnailRemoved,
      values,
    ],
  );

  useEffect(() => {
    if (allowOrganizationVisibility || values.isPublic) return;

    setField('isPublic', true);
  }, [allowOrganizationVisibility, setField, values.isPublic]);

  const goToNextStepFromDetails = () => {
    const nextErrors = validateDetails(values, type);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) setStep(hasContentStep ? 'content' : 'preview');
  };

  // An error is only visible on the step that renders its field. Details errors belong to
  // step 1; section text is editable on the content step (the arrange step shows it
  // read-only, so it cannot fix a length breach). Anything else has no step that can
  // display it — surfaced inline on preview instead, so submit is never blocked silently.
  const stepForErrors = (nextErrors: TContentFormErrors): WizardStep | undefined => {
    if (hasDetailsError(nextErrors)) return 'details';
    if (nextErrors.sections || hasArticleFieldError(nextErrors)) return 'content';
    return undefined;
  };

  const submit = async (mode: TContentFormMode) => {
    const nextErrors =
      (isCreateDraftComposer || isEditingDraft) && mode === 'draft' ? {} : validateForSubmit(values, type, mode);
    if ((isCreateArticle || (isEditingDraft && type === 'article')) && mode === 'draft') {
      const articleErrors = validateArticleSections(values.sections, { requireAll: false });
      Object.assign(nextErrors, articleErrors.fields);
      if (articleErrors.article) nextErrors.sections = articleErrors.article;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const targetStep = stepForErrors(nextErrors);
      if (targetStep) setStep(targetStep);
      return;
    }
    const snapshot = JSON.stringify(editableSnapshot(values));
    const submittedThumbnailRemoval = thumbnailRemoved;
    const saved = await onSubmit(mode, { values, thumbnailFile, removeThumbnail: submittedThumbnailRemoval });
    if ((isCreateDraftComposer || isEditingDraft) && mode === 'draft' && saved === true) {
      setSavedDraftSnapshot(snapshot);
      setSavedDraftThumbnail(thumbnailFile);
      setSavedDraftVideoFile(values.videoFile ?? null);
      setSavedDraftArticleFiles(articleFiles);
      setThumbnailRemoved((current) => (current === submittedThumbnailRemoval ? false : current));
    }
  };

  const goToNextStepFromContent = () => {
    const nextErrors = validateContentStep(values, type);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) setStep(afterContentStep);
  };

  // Errors with no editable step (e.g. a stored value a tightened limit now rejects).
  const unroutableError =
    Object.keys(errors).length > 0 && !stepForErrors(errors) ? Object.values(errors)[0] : undefined;

  const actionButton = (action: WizardFinalAction, width?: string) => (
    <CustomButton
      key={action.key}
      label={isCreatePost && action.mode === 'publish' ? 'Publish' : action.label}
      backgroundColor={action.backgroundColor}
      isTransparent={action.isTransparent}
      labelColor={
        action.isTransparent ? (isEditingDraft && !hasAuthoredChanges ? '#668DAF' : 'text.mediumBlue') : 'extra.white'
      }
      labelHoverColor={action.isTransparent ? 'extra.black' : undefined}
      disabled={isEditingDraft && action.mode === 'draft' && !hasAuthoredChanges}
      disabledColor={isEditingDraft && action.mode === 'draft' ? 'white' : undefined}
      isLoading={isSubmitting}
      onClick={() => submit(action.mode)}
      width={width ?? (isCreatePost ? '300px' : '295px')}
      style={isCreatePost ? { maxWidth: '100%' } : undefined}
    />
  );

  const continueButton = (onClick: () => void, disabled = false) => (
    <CustomButton
      label="Continue"
      backgroundColor="secondary.500"
      onClick={onClick}
      width={'295px'}
      labelColor="white"
      disabled={disabled}
    />
  );

  const goBackButton = (onClick: () => void) => (
    <CustomButton label="Go Back" isTransparent labelColor="background.blueGray" onClick={onClick} />
  );

  const contentStepByType: Partial<Record<TContentType, React.ReactNode>> = {
    article: (
      <ArticleSectionsEditor
        mode="edit"
        sections={values.sections}
        onChange={setSections}
        error={errors.sections}
        fieldErrors={errors}
      />
    ),
  };

  // Only articles have a content step. A post is a single block of text written as the
  // description on the details step, and a video's source is set there too.
  const hasContentStep = type === 'article';
  // After editing, articles go to the read-only "arrange" step.
  const afterContentStep: WizardStep = type === 'article' ? 'arrange' : 'preview';
  // The step immediately before preview — where "Go Back" from preview returns to.
  const beforePreviewStep: WizardStep = type === 'article' ? 'arrange' : 'details';

  const isMissingUploadedVideo =
    type === 'video' && values.videoSource === 'mux' && !values.videoFile && values.muxStatus === 'errored';
  const keepsExistingVideo = type === 'video' && keepsExistingRemoteVideo(values);
  const isDetailsContinueDisabled =
    (requireChangesToContinue && !hasAuthoredChanges) ||
    ((isCreateArticle || isCreatePost) && Object.keys(validateDetails(values, type)).length > 0) ||
    (type === 'video' &&
      !keepsExistingVideo &&
      ((values.videoSource === 'mux' && !values.videoFile) ||
        (values.videoSource === 'youtube' && !values.youtubeUrl) ||
        isVideoCompressing ||
        isYoutubeDurationChecking ||
        Boolean(values.videoProcessingError) ||
        isMissingUploadedVideo));

  return (
    <VStack align="center" width="100%" gap="24px" marginBottom="48px">
      {/* The other content flows keep their back and draft actions in the top bar. */}
      {!isCreateDraftComposer && (
        <HStack width="100%" justify="space-between" align="center" flexWrap="wrap" gap="12px">
          <CustomButton
            isTransparent
            iconBeforeLabelSvgPath={svgIcons.leftArrow}
            iconBeforeLabelSvgBoxHeight={12}
            iconBeforeLabelSvgColor={text.mediumBlue}
            labelColor="text.mediumBlue"
            labelHoverColor="extra.black"
            label="Back to Your Content"
            onClick={onExit}
          />
          {secondaryAction && actionButton(secondaryAction)}
        </HStack>
      )}

      {isCreateDraftComposer && !(isCreatePost && step === 'preview') ? (
        <HStack
          width="820px"
          maxWidth="100%"
          justify="space-between"
          align="flex-start"
          flexWrap="wrap"
          gap="20px"
          marginBottom="18px"
        >
          <VStack align="flex-start" gap="12px" flex="1" minWidth="280px">
            <HStack gap="16px">
              <Image
                src={
                  isCreateArticle ? '/svg/article-icon.svg' : isCreatePost ? '/svg/post-icon.svg' : '/svg/reel-icon.svg'
                }
                alt=""
                width="28px"
                height="28px"
              />
              <Text fontFamily="Lora" fontWeight={500} fontStyle="italic" fontSize="30px" color="text.darkBlue">
                {step === 'details' ? `Create ${getContentTypeLabel(type)}` : stepTitle(step, type)}
              </Text>
            </HStack>
            {step === 'details' && (
              <Text color="text.mediumBlue" fontSize="14px" fontWeight={600} lineHeight="1.2">
                Every person you’re about to support has worth, has value, and we thank you for dedicating your time to
                helping them become Whole.
              </Text>
            )}
          </VStack>
          <HStack gap="16px">
            {secondaryAction && (
              <Button
                height="54px"
                minWidth="150px"
                backgroundColor="white"
                color={hasAuthoredChanges ? 'text.darkBlue' : '#668DAF'}
                boxShadow="0px 2px 4px 0px #00417926"
                borderRadius="10px"
                fontWeight={700}
                _hover={{ backgroundColor: 'white' }}
                _disabled={{ opacity: 1, cursor: 'not-allowed', color: '#668DAF' }}
                isDisabled={!hasAuthoredChanges || isSubmitting || isVideoCompressing || isYoutubeDurationChecking}
                isLoading={isSubmitting}
                onClick={() => submit('draft')}
              >
                Save Draft
              </Button>
            )}
            {onDeleteDraft && (
              <Button
                height="54px"
                minWidth="130px"
                backgroundColor="white"
                color="extra.red"
                boxShadow="0px 2px 4px 0px #00417926"
                borderRadius="10px"
                fontWeight={700}
                _hover={{ backgroundColor: 'extra.red20pct' }}
                isDisabled={isSubmitting || isDeletingDraft}
                onClick={onDeleteDraft}
                aria-label="Delete Draft"
              >
                Remove
              </Button>
            )}
          </HStack>
        </HStack>
      ) : isCreatePost && step === 'preview' ? (
        <VStack gap="16px" textAlign="center" marginBottom="20px" maxWidth={'610px'}>
          <HStack gap="16px" justify="center">
            <Image src="/svg/post-icon.svg" alt="" width="28px" height="28px" />
            <Text fontFamily="Lora" fontWeight={500} fontStyle="italic" fontSize="30px" color="text.darkBlue">
              {step === 'preview' ? 'Post Preview' : 'Create Post'}
            </Text>
          </HStack>
        </VStack>
      ) : (
        <Text variant={'loraMediumTitle'} color={'extra.black'} fontStyle={'italic'} fontSize={'28px'} mb={'10px'}>
          {stepTitle(step, type)}
        </Text>
      )}

      {step === 'details' && (
        <>
          <DetailsStep
            type={type}
            isVideoSourceReadOnly={isVideoSourceReadOnly}
            values={values}
            errors={errors}
            authorLabel={authorLabel}
            thumbnailFile={thumbnailFile}
            defaultThumbnailUrl={thumbnailRemoved ? null : defaultThumbnailUrl}
            allowOrganizationVisibility={allowOrganizationVisibility}
            onField={setField}
            onDomainsChange={setDomains}
            onSubdomainsChange={setSubdomains}
            onThumbnailChange={(file) => {
              setThumbnailFile(file);
              if (file) setThumbnailRemoved(false);
            }}
            onThumbnailRemove={() => {
              setThumbnailFile(null);
              setThumbnailRemoved(canRemoveThumbnail && Boolean(defaultThumbnailUrl || savedDraftThumbnail));
            }}
            canRemoveThumbnail={canRemoveThumbnail}
            onVideoCompressingChange={setIsVideoCompressing}
            onYoutubeDurationCheckingChange={setIsYoutubeDurationChecking}
            isCreatePost={isCreatePost}
            isCreateReel={isCreateReel}
            isCreateArticle={isCreateArticle}
          />
          {isCreatePost ? (
            <VStack gap="22px" width="400px" maxWidth="100%" marginTop="32px">
              <CustomButton
                label="Preview Full Post"
                backgroundColor="white"
                borderColor={text.mediumBlue}
                borderHoverColor={text.mediumBlue}
                borderHoverWidth="2px"
                labelColor="text.darkBlue"
                onClick={goToNextStepFromDetails}
                disabled={isDetailsContinueDisabled}
                width="300px"
                style={{ maxWidth: '100%' }}
              />
              <Box width="100%" borderTop="2px solid" borderColor="background.gray" marginY="16px" />
              {actionButton(primaryAction)}
            </VStack>
          ) : (
            <VStack gap="18px" marginTop={isCreateDraftComposer ? '34px' : undefined}>
              {continueButton(goToNextStepFromDetails, isDetailsContinueDisabled)}
              {isCreateDraftComposer && goBackButton(onExit)}
            </VStack>
          )}
        </>
      )}

      {step === 'content' && (
        <>
          {contentStepByType[type]}
          <VStack gap="10px">
            {continueButton(goToNextStepFromContent)}
            {goBackButton(() => setStep('details'))}
          </VStack>
        </>
      )}

      {step === 'arrange' && (
        <>
          <ArticleSectionsEditor
            mode="arrange"
            sections={values.sections}
            onChange={setSections}
            error={errors.sections}
          />
          <VStack gap="10px">
            {continueButton(() => setStep('preview'))}
            {goBackButton(() => setStep('content'))}
          </VStack>
        </>
      )}

      {step === 'preview' && (
        <>
          <PreviewStep
            type={type}
            values={values}
            authorLabel={authorLabel}
            authorPhotoUrl={authorPhotoUrl}
            thumbnailFile={thumbnailFile}
            defaultThumbnailUrl={thumbnailRemoved ? null : defaultThumbnailUrl}
            isCreatePost={isCreatePost}
          />
          {unroutableError && (
            <Text color="extra.red" fontSize="13px" textAlign="center">
              {unroutableError}
            </Text>
          )}
          {isCreatePost ? (
            <HStack gap="40px" justify="center" flexWrap="wrap" width="100%" marginTop="16px">
              <CustomButton
                label="Edit"
                backgroundColor="white"
                borderColor={text.mediumBlue}
                borderHoverColor={text.mediumBlue}
                borderHoverWidth="2px"
                labelColor="text.darkBlue"
                onClick={() => setStep('details')}
                disabled={isSubmitting}
                width="145px"
                style={{ maxWidth: '100%' }}
                hoverOpacityDrop
              />
              {actionButton(primaryAction, '295px')}
            </HStack>
          ) : (
            <VStack gap="10px">
              {actionButton(primaryAction)}
              {goBackButton(() => setStep(beforePreviewStep))}
            </VStack>
          )}
        </>
      )}
    </VStack>
  );
};

export default ContentWizard;
